import React, { useEffect, useReducer } from 'react';
import api from '../api';
import { getError } from '../utils';
import MessageBox from '../components/general/MessageBox';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Nav } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import { toast } from 'react-toastify';
import Pagination from '../components/general/Pagination';
import LoadingLogo from '../components/general/LoadingLogo';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' }; // a kept error would outlive the next successful fetch (the render checks it after loading)
    case 'FETCH_SUCCESS':
      return {
        ...state,
        items: action.payload.items,
        page: action.payload.page,
        pages: action.payload.pages,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };

    case 'CREATE_REQUEST':
      return { ...state, loadingCreate: true };
    case 'CREATE_SUCCESS':
      return {
        ...state,
        loadingCreate: false,
      };
    case 'CREATE_FAIL':
      return { ...state, loadingCreate: false };

    default:
      return state;
  }
};

export default function StockScreen() {
  const [{ loading, error, items, pages }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
    items: [],
  });

  const navigate = useNavigate();

  const { search } = useLocation(); // gets the query parameters of the URL and saves them in the variable called 'search'
  const sp = new URLSearchParams(search); // creates a new instance of the URLSearchParams class, passing in the search string, wich allows to apply several methods to work with the query parameters
  const page = sp.get('page') || 1; // Retrieves the value of the 'page' query parameter (the number on wich page the user is on)

  const filter = sp.get('filter') || 'keycaps'; // the tab lives in the URL, so the edit screens can send the admin back to the tab they came from

  const modelMap = {
    keycaps: {
      endpoint: '/api/keycaps', // GET and POST endpoint
      singular: 'Keycap', // for UI strings
      adminPath: 'keycap', // part after /admin/…
      responseKey: 'keycap', // property that the backend returns
    },
    'cardboard-boxes': {
      endpoint: '/api/cardboard-boxes',
      singular: 'Karton',
      adminPath: 'cardboard-box',
      responseKey: 'cardboardBox',
    },
    foam: {
      endpoint: '/api/foam',
      singular: 'Foam-sheet',
      adminPath: 'foam',
      responseKey: 'foam',
    },
    'transfer-film': {
      endpoint: '/api/transfer-film',
      singular: 'Transfer-Film',
      adminPath: 'transfer-film',
      responseKey: 'transferFilm',
    },
    'keycap-pullers': {
      endpoint: '/api/keycap-pullers',
      singular: 'Keycap-Puller',
      adminPath: 'keycap-puller',
      responseKey: 'keycapPuller',
    },
    'shipping-boxes': {
      endpoint: '/api/shipping-boxes',
      singular: 'Shipping-Box',
      adminPath: 'shipping-box',
      responseKey: 'shippingBox',
    },
    ink: {
      endpoint: '/api/ink',
      singular: 'Ink',
      adminPath: 'ink',
      responseKey: 'ink',
    },
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const endpoint = modelMap[filter].endpoint; // every tab of the Nav has an entry, and the Nav and the edit screens send only those (a fallback for one without went unused, deleted Oct 2026); a hand-typed ?filter= outside the seven throws here and shows its TypeError as the error, left as is (user decision, Nov 2026: only a hand-typed admin URL reaches it)

        const { data } = await api.get(`${endpoint}?page=${page}`);

        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    };

    fetchData();
  }, [filter, page]);

  const createHandler = async () => {
    const { endpoint, singular, adminPath, responseKey } = modelMap[filter]; // every tab has an entry; a filter outside the seven would now throw here instead of toasting, and none reaches this button: a hand-typed one fails the fetch first, and the error replaces the button (a guard for it was dead, deleted Oct 2026)

    if (window.confirm(`Willst du ein neues ${singular} erstellen?`)) {
      try {
        dispatch({ type: 'CREATE_REQUEST' });
        const { data } = await api.post(endpoint, {});

        const created = responseKey ? data[responseKey] : data;
        const id = created?._id;

        toast.success(`${singular} erstellt`);
        dispatch({ type: 'CREATE_SUCCESS' });

        // send the user straight to the edit screen
        navigate(`/admin/${adminPath}/${id}?page=${page}`);
      } catch (err) {
        toast.error(getError(err));
        dispatch({ type: 'CREATE_FAIL' });
      }
    }
  };

  return (
    <Container>
      <div>
        <title>Stock</title>

        <h1 className="screen-heading">Stock</h1>

        {loading ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox>
        ) : (
          <>
            <Row className="my-3">
              <Nav
                justify
                variant="tabs"
                activeKey={filter}
                onSelect={(key) =>
                  navigate(`/admin/stock?filter=${key}&page=1`)
                }
                className="customizer-nav"
                style={{ fontSize: '18px', fontWeight: '600' }}
              >
                <Nav.Item>
                  <Nav.Link eventKey="keycaps">Keycaps</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="cardboard-boxes">
                    Cardboard Boxes
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="foam">Foam</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="transfer-film">Transfer Film</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="keycap-pullers">Keycap Pullers</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="shipping-boxes">Shipping Boxes</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="ink">Ink</Nav.Link>
                </Nav.Item>
              </Nav>

              {filter === 'keycaps' ? (
                <div>
                  <Row>
                    <Col>
                      <div className="d-flex justify-content-center my-5">
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={createHandler}
                        >
                          Keycap erstellen
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <Row className="d-xl-none">
                    {items ? (
                      items.map((keycap) => (
                        <Col key={keycap._id} xs={12} sm={12} md={12} lg={6}>
                          <Card className="products-card">
                            <Card.Body className="text-center">
                              <Card.Title className="code mb-4">
                                {keycap.profile}
                              </Card.Title>
                              <div className="divider"></div>
                              <div className="code-info">
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Width:
                                  </strong>{' '}
                                  {keycap.isIsoEnter ? (
                                    <span className="no-wrap">ISO Enter</span>
                                  ) : keycap.hasHomingBar ? (
                                    <span className="no-wrap">
                                      {keycap.width} (Homing Bar)
                                    </span>
                                  ) : keycap.isConvex &&
                                    keycap.isOffCentered ? (
                                    <span className="no-wrap">
                                      {keycap.width} (Convex + Off-Centered)
                                    </span>
                                  ) : keycap.isConvex ? (
                                    <span className="no-wrap">
                                      {keycap.width} (Convex)
                                    </span>
                                  ) : keycap.isStepped ? (
                                    <span className="no-wrap">
                                      {keycap.width} (Stepped)
                                    </span>
                                  ) : (
                                    <span className="no-wrap">
                                      {keycap.width}
                                    </span>
                                  )}
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Height:
                                  </strong>{' '}
                                  {keycap.isIsoEnter ? (
                                    <span className="no-wrap">ISO Enter</span>
                                  ) : keycap.hasHomingBar ? (
                                    <span className="no-wrap">
                                      {keycap.height} (Homing Bar)
                                    </span>
                                  ) : keycap.isConvex &&
                                    keycap.isOffCentered ? (
                                    <span className="no-wrap">
                                      {keycap.height} (Convex + Off-Centered)
                                    </span>
                                  ) : keycap.isConvex ? (
                                    <span className="no-wrap">
                                      {keycap.height} (Convex)
                                    </span>
                                  ) : keycap.isStepped ? (
                                    <span className="no-wrap">
                                      {keycap.height} (Stepped)
                                    </span>
                                  ) : (
                                    <span className="no-wrap">
                                      {keycap.height}
                                    </span>
                                  )}
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Color:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {keycap.color}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Stock:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {keycap.stockQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Reserved:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {keycap.reservedQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Sold:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {keycap.soldQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Aktionen:
                                  </strong>
                                  <span className="no-wrap w-100 mt-1">
                                    <Button
                                      variant="secondary"
                                      type="button"
                                      onClick={() =>
                                        navigate(
                                          `/admin/keycap/${keycap._id}?page=${page}`
                                        )
                                      }
                                    >
                                      Bearbeiten
                                    </Button>
                                    &nbsp;
                                    <Button
                                      variant="secondary"
                                      type="button"
                                      onClick={() => {
                                        toast.info(
                                          'This button is disabled, as Keycap documents can be referenced inside Layout documents and therefor the Layouts rely on the Keycap documents. Instead you have the opportunity to set the isActive state of this Keycap to false.'
                                        );
                                      }}
                                    >
                                      Löschen
                                    </Button>
                                  </span>
                                </Card.Text>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))
                    ) : (
                      <div className="w-100">
                        <MessageBox>no keycap found</MessageBox>
                      </div>
                    )}
                  </Row>
                  <div className="d-none d-xl-block">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>PROFILE</th>
                          <th>SIZE</th>
                          <th>WIDTH</th>
                          <th>LENGTH</th>
                          <th>HEIGHT</th>
                          <th>STOCK</th>
                          <th>RESERVED</th>
                          <th>SOLD</th>
                          <th>AKTIONEN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items ? (
                          items.map((keycap) => (
                            <tr key={keycap._id}>
                              <td>{keycap.profile}</td>
                              {keycap.isIsoEnter ? (
                                <td>ISO Enter</td>
                              ) : keycap.hasHomingBar ? (
                                <td>
                                  {keycap.width}{' '}
                                  <span style={{ fontSize: '12px' }}>
                                    Homing Bar
                                  </span>
                                </td>
                              ) : keycap.isConvex && keycap.isOffCentered ? (
                                <td>
                                  {keycap.width}{' '}
                                  <span style={{ fontSize: '12px' }}>
                                    Convex + Off-Centered
                                  </span>
                                </td>
                              ) : keycap.isConvex ? (
                                <td>
                                  {keycap.width}{' '}
                                  <span style={{ fontSize: '12px' }}>
                                    Convex
                                  </span>
                                </td>
                              ) : keycap.isStepped ? (
                                <td>
                                  {keycap.width}{' '}
                                  <span style={{ fontSize: '12px' }}>
                                    Stepped
                                  </span>
                                </td>
                              ) : (
                                <td>{keycap.width}</td>
                              )}
                              <td>{keycap.widthMM}</td>
                              <td>{keycap.lengthMM}</td>
                              <td>{keycap.heightMM}</td>
                              <td>{keycap.stockQuantity}</td>
                              <td>{keycap.reservedQuantity}</td>
                              <td>{keycap.soldQuantity}</td>
                              <td>
                                <Button
                                  variant="secondary"
                                  type="button"
                                  onClick={() =>
                                    navigate(
                                      `/admin/keycap/${keycap._id}?page=${page}`
                                    )
                                  }
                                >
                                  Bearbeiten
                                </Button>
                                &nbsp;
                                <Button
                                  variant="secondary"
                                  type="button"
                                  onClick={() => {
                                    toast.info(
                                      'This button is disabled, as Keycap documents can be referenced inside Layout documents and therefor the Layouts rely on the Keycap documents. Instead you have the opportunity to set the isActive state of this Keycap to false.'
                                    );
                                  }}
                                >
                                  Löschen
                                </Button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <div className="w-100">
                            <MessageBox>no keycaps found</MessageBox>
                          </div>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : filter === 'cardboard-boxes' ? (
                <div>
                  <Row>
                    <Col>
                      <div className="d-flex justify-content-center my-5">
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={createHandler}
                        >
                          Cardboard Box erstellen
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <Row className="d-xl-none">
                    {items ? (
                      items.map((cardboardBox) => (
                        <Col
                          key={cardboardBox._id}
                          xs={12}
                          sm={12}
                          md={12}
                          lg={6}
                        >
                          <Card className="products-card">
                            <Card.Body className="text-center">
                              <Card.Title className="code mb-4">
                                {cardboardBox.size}
                              </Card.Title>
                              <div className="divider"></div>
                              <div className="code-info">
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Length (inner):
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {cardboardBox.length}
                                    {' mm'}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Width (inner):
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {cardboardBox.width}
                                    {' mm'}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Height (inner):
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {cardboardBox.height}
                                    {' mm'}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Stock:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {cardboardBox.stockQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Reserved:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {cardboardBox.reservedQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Sold:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {cardboardBox.soldQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Aktionen:
                                  </strong>
                                  <span className="no-wrap w-100 mt-1">
                                    <Button
                                      variant="secondary"
                                      type="button"
                                      onClick={() =>
                                        navigate(
                                          `/admin/cardboard-box/${cardboardBox._id}?page=${page}`
                                        )
                                      }
                                    >
                                      Bearbeiten
                                    </Button>
                                    &nbsp;
                                    <Button
                                      variant="secondary"
                                      type="button"
                                      onClick={() => {
                                        toast.info(
                                          'This button is disabled, as CardboardBox documents can be referenced inside Layout documents and therefor the Layouts rely on the CardboardBox documents. Instead you have the opportunity to set the isActive state of this CardboardBox to false.'
                                        );
                                      }}
                                    >
                                      Löschen
                                    </Button>
                                  </span>
                                </Card.Text>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))
                    ) : (
                      <div className="w-100">
                        <MessageBox>no cardboard boxes found</MessageBox>
                      </div>
                    )}
                  </Row>
                  <div className="d-none d-xl-block">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>SIZE</th>
                          <th>
                            LENGTH{' '}
                            <span
                              style={{ fontWeight: 'normal', fontSize: '12px' }}
                            >
                              (inner)
                            </span>
                          </th>
                          <th>
                            WIDTH{' '}
                            <span
                              style={{ fontWeight: 'normal', fontSize: '12px' }}
                            >
                              (inner)
                            </span>
                          </th>
                          <th>
                            HEIGHT{' '}
                            <span
                              style={{ fontWeight: 'normal', fontSize: '12px' }}
                            >
                              (inner)
                            </span>
                          </th>
                          <th>STOCK</th>
                          <th>RESERVED</th>
                          <th>SOLD</th>
                          <th>AKTIONEN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items ? (
                          items.map((cardboardBox) => (
                            <tr key={cardboardBox._id}>
                              <td>{cardboardBox.size}</td>
                              <td>
                                <span className="no-wrap">
                                  {cardboardBox.length}
                                  {' mm'}
                                </span>
                              </td>
                              <td>
                                <span className="no-wrap">
                                  {cardboardBox.width}
                                  {' mm'}
                                </span>
                              </td>
                              <td>
                                <span className="no-wrap">
                                  {cardboardBox.height}
                                  {' mm'}
                                </span>
                              </td>
                              <td>{cardboardBox.stockQuantity}</td>
                              <td>{cardboardBox.reservedQuantity}</td>
                              <td>{cardboardBox.soldQuantity}</td>
                              <td>
                                <Button
                                  variant="secondary"
                                  type="button"
                                  onClick={() =>
                                    navigate(
                                      `/admin/cardboard-box/${cardboardBox._id}?page=${page}`
                                    )
                                  }
                                >
                                  Bearbeiten
                                </Button>
                                &nbsp;
                                <Button
                                  variant="secondary"
                                  type="button"
                                  onClick={() => {
                                    toast.info(
                                      'This button is disabled, as CardboardBox documents can be referenced inside Layout documents and therefor the Layouts rely on the CardboardBox documents. Instead you have the opportunity to set the isActive state of this CardboardBox to false.'
                                    );
                                  }}
                                >
                                  Löschen
                                </Button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <div className="w-100">
                            <MessageBox>no cardboard boxes found</MessageBox>
                          </div>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : filter === 'foam' ? (
                <div>
                  <Row>
                    <Col>
                      <div className="d-flex justify-content-center my-5">
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={createHandler}
                        >
                          Foam erstellen
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <Row className="d-xl-none">
                    {items ? (
                      items.map((foam) => (
                        <Col key={foam._id} xs={12} sm={12} md={12} lg={6}>
                          <Card className="products-card">
                            <Card.Body className="text-center">
                              <Card.Title className="code mb-4">
                                {foam.size} ({foam.type})
                              </Card.Title>
                              <div className="divider"></div>
                              <div className="code-info">
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Length:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {foam.length}
                                    {' mm'}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Width:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {foam.width}
                                    {' mm'}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Height:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {foam.height}
                                    {' mm'}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Stock:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {foam.stockQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Reserved:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {foam.reservedQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Sold:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {foam.soldQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Aktionen:
                                  </strong>
                                  <span className="no-wrap w-100 mt-1">
                                    <Button
                                      variant="secondary"
                                      type="button"
                                      onClick={() =>
                                        navigate(
                                          `/admin/foam/${foam._id}?page=${page}`
                                        )
                                      }
                                    >
                                      Bearbeiten
                                    </Button>
                                    &nbsp;
                                    <Button
                                      variant="secondary"
                                      type="button"
                                      onClick={() => {
                                        toast.info(
                                          'This button is disabled, as Foam documents can be referenced inside Layout documents and therefor the Layouts rely on the Foam documents. Instead you have the opportunity to set the isActive state of this Foam to false.'
                                        );
                                      }}
                                    >
                                      Löschen
                                    </Button>
                                  </span>
                                </Card.Text>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))
                    ) : (
                      <div className="w-100">
                        <MessageBox>no foam found</MessageBox>
                      </div>
                    )}
                  </Row>
                  <div className="d-none d-xl-block">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>SIZE</th>
                          <th>LENGTH</th>
                          <th>WIDTH</th>
                          <th>HEIGHT</th>
                          <th>STOCK</th>
                          <th>RESERVED</th>
                          <th>SOLD</th>
                          <th>AKTIONEN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items ? (
                          items.map((foam) => (
                            <tr key={foam._id}>
                              <td>
                                {foam.size} ({foam.type})
                              </td>
                              <td>
                                <span className="no-wrap">
                                  {foam.length}
                                  {' mm'}
                                </span>
                              </td>
                              <td>
                                <span className="no-wrap">
                                  {foam.width}
                                  {' mm'}
                                </span>
                              </td>
                              <td>
                                <span className="no-wrap">
                                  {foam.height}
                                  {' mm'}
                                </span>
                              </td>
                              <td>{foam.stockQuantity}</td>
                              <td>{foam.reservedQuantity}</td>
                              <td>{foam.soldQuantity}</td>
                              <td>
                                <Button
                                  variant="secondary"
                                  type="button"
                                  onClick={() =>
                                    navigate(
                                      `/admin/foam/${foam._id}?page=${page}`
                                    )
                                  }
                                >
                                  Bearbeiten
                                </Button>
                                &nbsp;
                                <Button
                                  variant="secondary"
                                  type="button"
                                  onClick={() => {
                                    toast.info(
                                      'This button is disabled, as Foam documents can be referenced inside Layout documents and therefor the Layouts rely on the Foam documents. Instead you have the opportunity to set the isActive state of this Foam to false.'
                                    );
                                  }}
                                >
                                  Löschen
                                </Button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <div className="w-100">
                            <MessageBox>no foam found</MessageBox>
                          </div>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : filter === 'transfer-film' ? (
                <div>
                  <Row>
                    <Col>
                      <div className="d-flex justify-content-center my-5">
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={createHandler}
                        >
                          Transfer Film erstellen
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <Row className="d-xl-none">
                    {items ? (
                      items.map((transferFilm) => (
                        <Col
                          key={transferFilm._id}
                          xs={12}
                          sm={12}
                          md={12}
                          lg={6}
                        >
                          <Card className="products-card">
                            <Card.Body className="text-center">
                              <Card.Title className="code mb-4">
                                {transferFilm.size}
                              </Card.Title>
                              <div className="divider"></div>
                              <div className="code-info">
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Stock:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {transferFilm.stockQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Reserved:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {transferFilm.reservedQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Sold:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {transferFilm.soldQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Aktionen:
                                  </strong>
                                  <span className="no-wrap w-100 mt-1">
                                    <Button
                                      variant="secondary"
                                      type="button"
                                      onClick={() =>
                                        navigate(
                                          `/admin/transfer-film/${transferFilm._id}?page=${page}`
                                        )
                                      }
                                    >
                                      Bearbeiten
                                    </Button>
                                    &nbsp;
                                    <Button
                                      variant="secondary"
                                      type="button"
                                      onClick={() => {
                                        toast.info(
                                          'This button is disabled, as TransferFilm documents can be referenced inside Layout documents and therefor the Layouts rely on the TransferFilm documents. Instead you have the opportunity to set the isActive state of this TransferFilm to false.'
                                        );
                                      }}
                                    >
                                      Löschen
                                    </Button>
                                  </span>
                                </Card.Text>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))
                    ) : (
                      <div className="w-100">
                        <MessageBox>no transfer film found</MessageBox>
                      </div>
                    )}
                  </Row>
                  <div className="d-none d-xl-block">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>SIZE</th>
                          <th>STOCK</th>
                          <th>RESERVED</th>
                          <th>SOLD</th>
                          <th>AKTIONEN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items ? (
                          items.map((transferFilm) => (
                            <tr key={transferFilm._id}>
                              <td>{transferFilm.size}</td>
                              <td>{transferFilm.stockQuantity}</td>
                              <td>{transferFilm.reservedQuantity}</td>
                              <td>{transferFilm.soldQuantity}</td>
                              <td>
                                <Button
                                  variant="secondary"
                                  type="button"
                                  onClick={() =>
                                    navigate(
                                      `/admin/transfer-film/${transferFilm._id}?page=${page}`
                                    )
                                  }
                                >
                                  Bearbeiten
                                </Button>
                                &nbsp;
                                <Button
                                  variant="secondary"
                                  type="button"
                                  onClick={() => {
                                    toast.info(
                                      'This button is disabled, as TransferFilm documents can be referenced inside Layout documents and therefor the Layouts rely on the TransferFilm documents. Instead you have the opportunity to set the isActive state of this TransferFilm to false.'
                                    );
                                  }}
                                >
                                  Löschen
                                </Button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <div className="w-100">
                            <MessageBox>no transfer film found</MessageBox>
                          </div>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : filter === 'keycap-pullers' ? (
                <div>
                  <Row>
                    <Col>
                      <div className="d-flex justify-content-center my-5">
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={createHandler}
                        >
                          Keycap Puller erstellen
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <Row className="d-xl-none">
                    {items ? (
                      items.map((puller) => (
                        <Col key={puller._id} xs={12} sm={12} md={12} lg={6}>
                          <Card className="products-card">
                            <Card.Body className="text-center">
                              <Card.Title className="code mb-4">
                                {puller.manufacturer}
                              </Card.Title>
                              <div className="divider"></div>
                              <div className="code-info">
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Stock:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {puller.stockQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Reserved:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {puller.reservedQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Sold:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {puller.soldQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Aktionen:
                                  </strong>
                                  <span className="no-wrap w-100 mt-1">
                                    <Button
                                      variant="secondary"
                                      type="button"
                                      onClick={() =>
                                        navigate(
                                          `/admin/keycap-puller/${puller._id}?page=${page}`
                                        )
                                      }
                                    >
                                      Bearbeiten
                                    </Button>
                                    &nbsp;
                                    <Button
                                      variant="secondary"
                                      type="button"
                                      onClick={() => {
                                        toast.info(
                                          'This button is disabled, as Keycap Puller documents may be referenced inside other documents. Instead you have the opportunity to set the isActive state of this Keycap Puller to false.'
                                        );
                                      }}
                                    >
                                      Löschen
                                    </Button>
                                  </span>
                                </Card.Text>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))
                    ) : (
                      <div className="w-100">
                        <MessageBox>no keycap puller found</MessageBox>
                      </div>
                    )}
                  </Row>
                  <div className="d-none d-xl-block">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>MANUFACTURER</th>
                          <th>STOCK</th>
                          <th>RESERVED</th>
                          <th>SOLD</th>
                          <th>AKTIONEN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items ? (
                          items.map((puller) => (
                            <tr key={puller._id}>
                              <td>{puller.manufacturer}</td>
                              <td>{puller.stockQuantity}</td>
                              <td>{puller.reservedQuantity}</td>
                              <td>{puller.soldQuantity}</td>
                              <td>
                                <Button
                                  variant="secondary"
                                  type="button"
                                  onClick={() =>
                                    navigate(
                                      `/admin/keycap-puller/${puller._id}?page=${page}`
                                    )
                                  }
                                >
                                  Bearbeiten
                                </Button>
                                &nbsp;
                                <Button
                                  variant="secondary"
                                  type="button"
                                  onClick={() => {
                                    toast.info(
                                      'This button is disabled, as KeycapPuller documents may be referenced inside other documents. Instead you have the opportunity to set the isActive state of this KeycapPuller to false.'
                                    );
                                  }}
                                >
                                  Löschen
                                </Button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <div className="w-100">
                            <MessageBox>no keycap puller found</MessageBox>
                          </div>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : filter === 'shipping-boxes' ? (
                <div>
                  <Row>
                    <Col>
                      <div className="d-flex justify-content-center my-5">
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={createHandler}
                        >
                          Shipping Box erstellen
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <Row className="d-xl-none">
                    {items ? (
                      items.map((box) => (
                        <Col key={box._id} xs={12} sm={12} md={12} lg={6}>
                          <Card className="products-card">
                            <Card.Body className="text-center">
                              <Card.Title className="code mb-4">
                                {box.size}
                              </Card.Title>
                              <div className="divider"></div>
                              <div className="code-info">
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Length (outside):
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {box.length}
                                    {' mm'}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Width (outside):
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {box.width}
                                    {' mm'}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Height (outside):
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {box.height}
                                    {' mm'}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Stock:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {box.stockQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Reserved:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {box.reservedQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Sold:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {box.soldQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Aktionen:
                                  </strong>
                                  <span className="no-wrap w-100 mt-1">
                                    <Button
                                      variant="secondary"
                                      type="button"
                                      onClick={() =>
                                        navigate(
                                          `/admin/shipping-box/${box._id}?page=${page}`
                                        )
                                      }
                                    >
                                      Bearbeiten
                                    </Button>
                                    &nbsp;
                                    <Button
                                      variant="secondary"
                                      type="button"
                                      onClick={() => {
                                        toast.info(
                                          'This button is disabled, as Shipping Box documents may be referenced inside other documents. Instead you have the opportunity to set the isActive state of this ShippingBox to false.'
                                        );
                                      }}
                                    >
                                      Löschen
                                    </Button>
                                  </span>
                                </Card.Text>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))
                    ) : (
                      <div className="w-100">
                        <MessageBox>no shipping box found</MessageBox>
                      </div>
                    )}
                  </Row>
                  <div className="d-none d-xl-block">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>SIZE</th>
                          <th>
                            LENGTH{' '}
                            <span
                              style={{ fontWeight: 'normal', fontSize: '12px' }}
                            >
                              (outside)
                            </span>
                          </th>
                          <th>
                            WIDTH{' '}
                            <span
                              style={{ fontWeight: 'normal', fontSize: '12px' }}
                            >
                              (outside)
                            </span>
                          </th>
                          <th>
                            HEIGHT{' '}
                            <span
                              style={{ fontWeight: 'normal', fontSize: '12px' }}
                            >
                              (outside)
                            </span>
                          </th>
                          <th>STOCK</th>
                          <th>RESERVED</th>
                          <th>SOLD</th>
                          <th>AKTIONEN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items ? (
                          items.map((box) => (
                            <tr key={box._id}>
                              <td>{box.size}</td>
                              <td>
                                <span className="no-wrap">
                                  {box.length}
                                  {' mm'}
                                </span>
                              </td>
                              <td>
                                <span className="no-wrap">
                                  {box.width}
                                  {' mm'}
                                </span>
                              </td>
                              <td>
                                <span className="no-wrap">
                                  {box.height}
                                  {' mm'}
                                </span>
                              </td>
                              <td>{box.stockQuantity}</td>
                              <td>{box.reservedQuantity}</td>
                              <td>{box.soldQuantity}</td>
                              <td>
                                <Button
                                  variant="secondary"
                                  type="button"
                                  onClick={() =>
                                    navigate(
                                      `/admin/shipping-box/${box._id}?page=${page}`
                                    )
                                  }
                                >
                                  Bearbeiten
                                </Button>
                                &nbsp;
                                <Button
                                  variant="secondary"
                                  type="button"
                                  onClick={() => {
                                    toast.info(
                                      'This button is disabled, as ShippingBox documents may be referenced inside other documents. Instead you have the opportunity to set the isActive state of this ShippingBox to false.'
                                    );
                                  }}
                                >
                                  Löschen
                                </Button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <div className="w-100">
                            <MessageBox>no shipping box found</MessageBox>
                          </div>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : filter === 'ink' ? (
                <div>
                  <Row>
                    <Col>
                      <div className="d-flex justify-content-center my-5">
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={createHandler}
                        >
                          Ink erstellen
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <Row className="d-xl-none">
                    {items ? (
                      items.map((ink) => (
                        <Col key={ink._id} xs={12} sm={12} md={12} lg={6}>
                          <Card className="products-card">
                            <Card.Body className="text-center">
                              <Card.Title className="code mb-4">
                                {ink.color}
                              </Card.Title>
                              <div className="divider"></div>
                              <div className="code-info">
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Volume:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {ink.volume}
                                    {' ml'}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Stock:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {ink.stockQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Reserved:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {ink.reservedQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Sold:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {ink.soldQuantity}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Aktionen:
                                  </strong>
                                  <span className="no-wrap w-100 mt-1">
                                    <Button
                                      variant="secondary"
                                      type="button"
                                      onClick={() =>
                                        navigate(
                                          `/admin/ink/${ink._id}?page=${page}`
                                        )
                                      }
                                    >
                                      Bearbeiten
                                    </Button>
                                    &nbsp;
                                    <Button
                                      variant="secondary"
                                      type="button"
                                      onClick={() => {
                                        toast.info(
                                          'This button is disabled, as Ink documents may be referenced inside other documents. Instead you have the opportunity to set the isActive state of this Ink to false.'
                                        );
                                      }}
                                    >
                                      Löschen
                                    </Button>
                                  </span>
                                </Card.Text>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))
                    ) : (
                      <div className="w-100">
                        <MessageBox>no ink found</MessageBox>
                      </div>
                    )}
                  </Row>
                  <div className="d-none d-xl-block">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>COLOR</th>
                          <th>VOLUME</th>
                          <th>STOCK</th>
                          <th>RESERVED</th>
                          <th>SOLD</th>
                          <th>AKTIONEN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items ? (
                          items.map((ink) => (
                            <tr key={ink._id}>
                              <td>{ink.color}</td>
                              <td>
                                <span className="no-wrap">
                                  {ink.volume}
                                  {' ml'}
                                </span>
                              </td>
                              <td>{ink.stockQuantity}</td>
                              <td>{ink.reservedQuantity}</td>
                              <td>{ink.soldQuantity}</td>
                              <td>
                                <Button
                                  variant="secondary"
                                  type="button"
                                  onClick={() =>
                                    navigate(
                                      `/admin/ink/${ink._id}?page=${page}`
                                    )
                                  }
                                >
                                  Bearbeiten
                                </Button>
                                &nbsp;
                                <Button
                                  variant="secondary"
                                  type="button"
                                  onClick={() => {
                                    toast.info(
                                      'This button is disabled, as Ink documents may be referenced inside other documents. Instead you have the opportunity to set the isActive state of this Ink to false.'
                                    );
                                  }}
                                >
                                  Löschen
                                </Button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <div className="w-100">
                            <MessageBox>no ink found</MessageBox>
                          </div>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div></div>
              )}
            </Row>

            <Pagination
              pages={pages}
              page={Number(page)}
              baseUrl="/admin/stock"
              filterParam={filter}
              search={null}
            />
          </>
        )}
      </div>
    </Container>
  );
}
